
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/i18n/useTranslation";
import { TaskRole } from "@/components/scheduler/schedulerTypes";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { 
  Filter, 
  Code, 
  Square, 
  Box, 
  LayoutDashboard, 
  PenTool,
  Headphones,
  Wrench,
  CheckCircle,
  Settings
} from "lucide-react";

interface RoleFilterProps {
  selectedRole: TaskRole | 'all';
  setSelectedRole: (role: TaskRole | 'all') => void;
  sidebarExpanded: boolean;
}

export const roleDefinitions: Record<TaskRole | 'all', {
  color: string; 
  bgColor: string;
  icon: React.ComponentType<any>;
  labelKey: string;
  displayName: string;
}> = {
  all: { 
    color: 'bg-gray-500', 
    bgColor: 'bg-gray-100',
    icon: Filter,
    labelKey: 'board.role.all',
    displayName: 'All Tasks'
  },
  Dev: { 
    color: 'bg-indigo-500', 
    bgColor: 'bg-indigo-100',
    icon: Code,
    labelKey: 'board.role.dev',
    displayName: 'Dev'
  },
  '2D': { 
    color: 'bg-purple-500', 
    bgColor: 'bg-purple-100',
    icon: Square,
    labelKey: 'board.role.2d',
    displayName: '2D'
  },
  '3D': { 
    color: 'bg-amber-500', 
    bgColor: 'bg-amber-100',
    icon: Box,
    labelKey: 'board.role.3d',
    displayName: '3D'
  },
  UI: { 
    color: 'bg-blue-500', 
    bgColor: 'bg-blue-100',
    icon: LayoutDashboard, 
    labelKey: 'board.role.ui',
    displayName: 'UI'
  },
  Design: { 
    color: 'bg-teal-500', 
    bgColor: 'bg-teal-100',
    icon: PenTool, 
    labelKey: 'board.role.design',
    displayName: 'Design'
  },
  Audio: { 
    color: 'bg-red-500', 
    bgColor: 'bg-red-100',
    icon: Headphones, 
    labelKey: 'board.role.audio',
    displayName: 'Audio'
  },
  'Tech Art': { 
    color: 'bg-emerald-500', 
    bgColor: 'bg-emerald-100',
    icon: Wrench, 
    labelKey: 'board.role.techart',
    displayName: 'Tech Art'
  },
  QA: { 
    color: 'bg-green-500', 
    bgColor: 'bg-green-100',
    icon: CheckCircle, 
    labelKey: 'board.role.qa',
    displayName: 'QA'
  },
  Admin: { 
    color: 'bg-cyan-500', 
    bgColor: 'bg-cyan-100',
    icon: Settings, 
    labelKey: 'board.role.admin',
    displayName: 'Admin'
  },
};

export function RoleFilter({ selectedRole, setSelectedRole, sidebarExpanded }: RoleFilterProps) {
  const { t } = useTranslation();

  return (
    <>
      <div className={`${sidebarExpanded ? 'px-2' : 'px-0'}`}>
        <h3 className={`text-sm font-semibold ${sidebarExpanded ? '' : 'text-center'}`}>
          {sidebarExpanded ? t('board.roles') : ''}
        </h3>
      </div>
      <div className="mt-2">
        {(Object.keys(roleDefinitions) as Array<keyof typeof roleDefinitions>).map((role) => {
          const { icon: Icon, color, displayName } = roleDefinitions[role];
          const isSelected = selectedRole === role;
          
          return (
            <Button
              key={role}
              variant="ghost"
              className={`${sidebarExpanded ? 'w-full justify-start' : 'w-full p-2 justify-center'}`}
              onClick={() => setSelectedRole(role)}
              title={displayName}
            >
              <div className={`flex items-center ${sidebarExpanded && isSelected ? 'w-full' : ''}`}>
                <div className={`${color} rounded-md ${isSelected ? (sidebarExpanded ? 'p-2' : 'p-3') : 'p-1'} flex items-center justify-left transition-all duration-200 ${sidebarExpanded && isSelected ? 'w-full' : ''}`}>
                  <Icon className="h-4 w-4 text-white" />
                  {sidebarExpanded && isSelected && (
                    <span className="ml-2 text-white text-left">{displayName}</span>
                  )}
                </div>
                {sidebarExpanded && !isSelected && <span className="ml-2 text-left">{displayName}</span>}
              </div>
            </Button>
          );
        })}
      </div>

      <div className="md:hidden flex flex-wrap p-3 border-b border-border mt-4">
        <ToggleGroup type="single" value={selectedRole} onValueChange={(value) => value && setSelectedRole(value as TaskRole | 'all')}>
          {(Object.keys(roleDefinitions) as Array<keyof typeof roleDefinitions>).map((role) => {
            const { icon: Icon, color } = roleDefinitions[role];
            const isSelected = selectedRole === role;
            
            return (
              <ToggleGroupItem key={role} value={role} aria-label={roleDefinitions[role].displayName}>
                <div className={`${color} rounded-md ${isSelected ? 'p-4 flex items-center' : 'p-1'} transition-all duration-200`}>
                  <Icon className="h-3.5 w-3.5 text-white" />
                </div>
              </ToggleGroupItem>
            );
          })}
        </ToggleGroup>
      </div>
    </>
  );
}
